/**
 * React component that renders the mini Hotel card
 *
 * Copyright (c) 2020 Propellant Software Corp
 * Author: Yucho Ho
 */
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { Rating, Star } from '@smastrom/react-rating'
import MiniCard from '../MiniCard'
import { Title, CoverImage, BtnLink } from '../MiniCardUIComponents'
import styles from './MiniPartnerCard.module.scss'

const MiniPartnerCard = ({
  className,
  type = 'hotel',
  companyName,
  topHeader,
  bottomHeader,
  title,
  price,
  location,
  photos,
  actionButton,
  rating,
  ratingImageUrl,
  numReviews,
  size,
  fluid,
  onSeeDetailsClick,
  onClick,
  onMouseEnter,
  onMouseLeave
}) => {
  const isLarge = size === 'lg'
  const PartnerImage = useMemo(() => {
    let defaultPlaceholderImage = 'hotel-building'
    let photoUrl = null

    if (photos?.length > 0) {
      const { images } = photos[0]

      if (size === 'lg') {
        photoUrl = images?.large?.url
      } else {
        photoUrl = images?.medium?.url
      }
    }

    if (type === 'attraction') {
      defaultPlaceholderImage = 'empty-street'
    }

    return (
      <CoverImage
        photo={photoUrl}
        defaultImage={`/static/assets/img/illustrations/${defaultPlaceholderImage}.svg`} />
    )
  }, [type, photos, size])

  const Header = useMemo(() => {
    const className = cx(styles['header'], {
      [styles['header-sm']]: !isLarge
    })

    return (
      <>
        {topHeader && <div className={className}>{topHeader}</div>}
        {bottomHeader && <div className={className}>{bottomHeader}</div>}
      </>
    )
  }, [topHeader, bottomHeader, isLarge])

  const PartnerRating = useMemo(() => {
    if (ratingImageUrl) {
      return (
        <div
          title={`${companyName} Traveler Rating`}
          className={cx('d-flex', {
            'flex-row align-items-end': isLarge,
            'flex-column align-items-start': !isLarge
          })}>
          <img
            className={cx(styles['rating-img'], {
              [styles['rating-small-img']]: !isLarge,
              [styles['rating-margins']]: !isLarge
            })}
            src={ratingImageUrl} />
          <h6 className={cx({
            'font-xxs': !isLarge,
            'font-xs': isLarge
          })}>{numReviews} reviews</h6>
        </div>
      )
    } else if (rating >= 0) {
      return (
        <div title={`${companyName} Traveler Rating`} className='d-flex align-items-center'>
          <Rating
            className={cx(styles['ratings-container'])}
            value={rating}
            itemStyles={{
              itemShapes: Star,
              activeFillColor: styles.ratingActiveColor,
              inactiveFillColor: styles.ratingInactiveColor
            }}
            readOnly />
          <h6 className={cx(styles['rating-text'], 'font-small muted ml-1')}>{rating}</h6>
        </div>
      )
    }

    return null
  }, [companyName, rating, ratingImageUrl, numReviews, isLarge])

  const PartnerTitle = useMemo(() => (
    <div className='title-container'>
      {isLarge && (
        <Title
          className='dark-gray'
          tagname='h4'
          size={isLarge ? 'small' : 'xs'}>
          {location}
        </Title>
      )}
      <Title
        className='font-weight-semibold'
        size={isLarge ? 'h6' : 'small'}
        mb={isLarge ? '1' : '0'}>
        {title}
      </Title>
      {PartnerRating}
    </div>
  ), [isLarge, title, location, PartnerRating])

  const LeftFooterContent = useMemo(() => {
    return (onSeeDetailsClick && !price) ? (
      <BtnLink title={`See ${type} details`} onClick={onSeeDetailsClick}>
        See details
      </BtnLink>
    ) : (
      <span className='d-flex align-items-center'>
        {price && (
          <p className={cx('mb-0 mr-1', {
            'font-large': isLarge,
            'font-xxs': !isLarge
          })}>
            <span className='font-weight-semibold'>${price}</span>
            <span className='align-middle'>/night</span>
          </p>
        )}
      </span>
    )
  }, [type, onSeeDetailsClick, isLarge, price])

  return (
    <MiniCard
      className={cx(styles['mini-partner-card'], className, {
        [`card-${size}`]: !!size,
        [styles[`card-${size}`]]: !!size
      })}
      image={PartnerImage}
      header={Header}
      size={size}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      fluid={fluid}>
      <div className='d-flex flex-column flex-fill justify-content-between'>
        {PartnerTitle}
        <div className={cx('d-flex flex-row flex-wrap align-items-center justify-content-between')}>
          {LeftFooterContent}
          {actionButton}
        </div>
      </div>
    </MiniCard>
  )
}

MiniPartnerCard.propTypes = {
  className: PropTypes.string,
  type: PropTypes.oneOf(['hotel', 'attraction']),
  companyName: PropTypes.string,
  topHeader: PropTypes.node,
  bottomHeader: PropTypes.node,
  actionButton: PropTypes.node,
  title: PropTypes.string.isRequired,
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  location: PropTypes.string.isRequired,
  photos: PropTypes.array,
  rating: PropTypes.number,
  ratingImageUrl: PropTypes.string,
  numReviews: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'lg']),
  fluid: PropTypes.bool,
  onSeeDetailsClick: PropTypes.func,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func
}

export default MiniPartnerCard
